import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Link, router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../../components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../../components/ui/dialog'
import { TriangleAlertIcon, UserXIcon, ArrowRightLeftIcon, SearchIcon, BuildingIcon, UsersIcon, Loader2Icon, ShieldAlertIcon } from 'lucide-react'

interface Organization {
  id: number
  name: string
}

interface SecurityViolation {
  id: number
  event_type: string
  reason: string | null
  command_preview: string | null
  project_name: string | null
  created_at: string
}

interface OrganizationSearchResult {
  id: number
  name: string
  member_count: number
  admin_emails: string[]
}

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  organization_role: string
  super_admin?: boolean
  activated?: boolean
  activated_at?: string
  deactivated_at?: string
  created_at: string
  updated_at: string
  last_sign_in_at?: string
  sso_provider?: string
  workos_id?: string
  organization?: Organization
  can_impersonate?: boolean
}

interface AdminUserShowProps {
  user: User
  security_violations: SecurityViolation[]
  organization_deactivated: boolean
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Show({ user, security_violations, organization_deactivated }: AdminUserShowProps) {
  const { t } = useTranslation('admin')
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [isDeactivatingOrg, setIsDeactivatingOrg] = useState(false)
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false)
  const [moveSearchQuery, setMoveSearchQuery] = useState('')
  const [moveSearchResults, setMoveSearchResults] = useState<OrganizationSearchResult[]>([])
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSearchResult | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced search for organizations
  const searchOrganizations = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (query.length < 2) {
      setMoveSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/admin/organizations/search?q=${encodeURIComponent(query)}`, {
          headers: {
            'Accept': 'application/json',
          },
        })
        const results: OrganizationSearchResult[] = await response.json()
        // Filter out the user's current organization
        const filtered = results.filter(org => org.id !== user.organization?.id)
        setMoveSearchResults(filtered)
      } catch {
        setMoveSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }, [user.organization?.id])

  useEffect(() => {
    searchOrganizations(moveSearchQuery)
  }, [moveSearchQuery, searchOrganizations])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const handleMoveToOrganization = () => {
    if (!selectedOrg) return

    setIsMoving(true)
    router.post(`/admin/users/${user.id}/move_to_organization`, {
      organization_id: selectedOrg.id
    }, {
      onFinish: () => {
        setIsMoving(false)
        setIsMoveDialogOpen(false)
        setSelectedOrg(null)
        setMoveSearchQuery('')
        setMoveSearchResults([])
      }
    })
  }

  const handleDialogOpenChange = (open: boolean) => {
    setIsMoveDialogOpen(open)
    if (!open) {
      setSelectedOrg(null)
      setMoveSearchQuery('')
      setMoveSearchResults([])
    }
  }

  const handleImpersonate = () => {
    setIsImpersonating(true)
    // Use fetch to make a POST request, then do a full page reload
    fetch(`/admin/users/${user.id}/impersonate`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Content-Type': 'application/json',
      },
    }).then(() => {
      // Force full page reload to show impersonation banner
      window.location.href = '/'
    }).catch(() => {
      setIsImpersonating(false)
    })
  }

  const handleDeactivate = () => {
    setIsDeactivating(true)
    router.post(`/admin/users/${user.id}/deactivate`, {}, {
      onFinish: () => setIsDeactivating(false)
    })
  }

  const handleDeactivateOrganization = () => {
    setIsDeactivatingOrg(true)
    router.post(`/admin/users/${user.id}/deactivate_organization`, {}, {
      onFinish: () => setIsDeactivatingOrg(false)
    })
  }

  // Use can_impersonate from backend (already checked via Pundit policy)
  const canImpersonate = user.can_impersonate
  const isDeactivated = !!user.deactivated_at

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('users.index.title'), href: '/admin/users' }, { label: t('users.show.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {user.first_name} {user.last_name}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('users.show.description')}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/admin/users">{t('users.show.backToUsers')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('users.show.info.title')}</CardTitle>
                <CardDescription>{t('users.show.info.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex-shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-xl">
                    {user.first_name[0]}{user.last_name[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      {user.first_name} {user.last_name}
                    </h2>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.orgRole')}</p>
                    <div className="mt-1">
                      {user.organization_role === 'admin' ? (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                          {t('users.show.badges.admin')}
                        </Badge>
                      ) : user.organization_role === 'external' ? (
                        <Badge variant="outline">
                          {t('users.show.badges.external')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-muted text-foreground hover:bg-muted">
                          {t('users.show.badges.member')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.organization')}</p>
                    <p className="mt-1 text-sm">
                      {user.organization ? (
                        <Link href={`/admin/organizations/${user.organization.id}`} className="text-primary hover:underline">
                          {user.organization.name}
                        </Link>
                      ) : t('users.show.info.none')}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.accountCreated')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(user.created_at).toLocaleDateString(getDateLocale(), {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.lastSignIn')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {user.last_sign_in_at
                        ? new Date(user.last_sign_in_at).toLocaleDateString(getDateLocale(), {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : t('users.show.info.never')}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.userId')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">{user.id}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.accountStatus')}</p>
                    <div className="mt-1">
                      {user.deactivated_at ? (
                        <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
                          {t('users.show.badges.deactivated')}
                        </Badge>
                      ) : user.activated ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
                          {t('users.show.badges.activated')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                          {t('users.show.badges.pending')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.activatedAt')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {user.activated_at
                        ? new Date(user.activated_at).toLocaleDateString(getDateLocale(), {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : t('users.show.info.notActivated')}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.authType')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {user.workos_id ? t('users.show.info.sso') : t('users.show.info.password')}
                    </p>
                  </div>

                  {user.sso_provider && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('users.show.info.ssoProvider')}</p>
                      <p className="mt-1 text-sm text-foreground capitalize">{user.sso_provider}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Security Violations Section */}
            {security_violations.length > 0 && (
              <Card className="shadow-none border-red-200 bg-red-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-800">
                    <ShieldAlertIcon className="h-5 w-5" />
                    {t('users.show.securityViolations.title')}
                  </CardTitle>
                  <CardDescription className="text-red-700">
                    {t('users.show.securityViolations.description', { count: security_violations.length })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-red-200">
                          <th className="text-left py-2 px-3 font-medium text-red-800">{t('users.show.securityViolations.table.date')}</th>
                          <th className="text-left py-2 px-3 font-medium text-red-800">{t('users.show.securityViolations.table.eventType')}</th>
                          <th className="text-left py-2 px-3 font-medium text-red-800">{t('users.show.securityViolations.table.project')}</th>
                          <th className="text-left py-2 px-3 font-medium text-red-800">{t('users.show.securityViolations.table.reason')}</th>
                          <th className="text-left py-2 px-3 font-medium text-red-800">{t('users.show.securityViolations.table.command')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {security_violations.map((violation) => (
                          <tr key={violation.id} className="hover:bg-red-100/50">
                            <td className="py-2 px-3 text-red-900 whitespace-nowrap">
                              {new Date(violation.created_at).toLocaleString(getDateLocale(), {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="py-2 px-3">
                              <Badge variant="secondary" className="bg-red-200 text-red-900 hover:bg-red-200">
                                {violation.event_type}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-red-900">
                              {violation.project_name || '-'}
                            </td>
                            <td className="py-2 px-3 text-red-900 max-w-xs truncate">
                              {violation.reason || '-'}
                            </td>
                            <td className="py-2 px-3 text-red-900 font-mono text-xs max-w-xs truncate">
                              {violation.command_preview || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Deactivate Organization Button */}
                  {!organization_deactivated && user.organization && (
                    <div className="mt-4 pt-4 border-t border-red-200">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            className="w-full"
                            disabled={isDeactivatingOrg}
                          >
                            <ShieldAlertIcon className="mr-2 h-4 w-4" />
                            {isDeactivatingOrg ? t('users.show.securityViolations.deactivating') : t('users.show.securityViolations.deactivateOrg')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader className="items-center">
                            <div className="bg-red-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                              <ShieldAlertIcon className="text-red-600 size-6" />
                            </div>
                            <AlertDialogTitle>{t('users.show.securityViolations.dialog.title', { name: user.organization.name })}</AlertDialogTitle>
                            <AlertDialogDescription className="text-center">
                              {t('users.show.securityViolations.dialog.description')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('users.show.securityViolations.dialog.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700 text-white"
                              onClick={handleDeactivateOrganization}
                              disabled={isDeactivatingOrg}
                            >
                              {isDeactivatingOrg ? t('users.show.securityViolations.dialog.confirming') : t('users.show.securityViolations.dialog.confirm')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}

                  {organization_deactivated && (
                    <div className="mt-4 pt-4 border-t border-red-200">
                      <Badge variant="secondary" className="w-full justify-center py-2 bg-red-200 text-red-900">
                        {t('users.show.securityViolations.orgDeactivated')}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('users.show.actions.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {canImpersonate && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10"
                        disabled={isImpersonating}
                      >
                        {isImpersonating ? t('users.show.actions.impersonateStarting') : t('users.show.actions.impersonate')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader className="items-center">
                        <div className="bg-primary/10 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                          <TriangleAlertIcon className="text-primary size-6" />
                        </div>
                        <AlertDialogTitle>{t('users.show.impersonateDialog.title', { firstName: user.first_name, lastName: user.last_name })}</AlertDialogTitle>
                        <AlertDialogDescription className="text-center">
                          {t('users.show.impersonateDialog.description', { firstName: user.first_name, lastName: user.last_name, email: user.email })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('users.show.impersonateDialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-primary hover:bg-primary/90 text-primary-foreground"
                          onClick={handleImpersonate}
                          disabled={isImpersonating}
                        >
                          {isImpersonating ? t('users.show.impersonateDialog.confirming') : t('users.show.impersonateDialog.confirm')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href={`/admin/users/${user.id}/edit`}>{t('users.show.actions.editUser')}</Link>
                </Button>
                <Dialog open={isMoveDialogOpen} onOpenChange={handleDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <ArrowRightLeftIcon className="mr-2 h-4 w-4" />
                      {t('users.show.moveDialog.button')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>{t('users.show.moveDialog.title')}</DialogTitle>
                      <DialogDescription>
                        {t('users.show.moveDialog.description')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Current organization info */}
                      {user.organization && (
                        <div className="text-sm text-muted-foreground">
                          {t('users.show.moveDialog.currentlyIn')} <strong>{user.organization.name}</strong>
                        </div>
                      )}

                      {/* Search input */}
                      <div className="relative">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={t('users.show.moveDialog.searchPlaceholder')}
                          value={moveSearchQuery}
                          onChange={(e) => setMoveSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>

                      {/* Search results */}
                      <div className="max-h-60 overflow-y-auto rounded-md border">
                        {isSearching ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : moveSearchResults.length > 0 ? (
                          <div className="divide-y">
                            {moveSearchResults.map((org) => (
                              <button
                                key={org.id}
                                type="button"
                                onClick={() => setSelectedOrg(org)}
                                className={`w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors ${
                                  selectedOrg?.id === org.id ? 'bg-primary/10 border-l-2 border-primary' : ''
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <BuildingIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{org.name}</div>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                      <span className="flex items-center gap-1">
                                        <UsersIcon className="h-3 w-3" />
                                        {t('users.show.moveDialog.memberCount', { count: org.member_count })}
                                      </span>
                                      {org.admin_emails.length > 0 && (
                                        <span className="truncate">
                                          {t('users.show.moveDialog.adminLabel')} {org.admin_emails[0]}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : moveSearchQuery.length >= 2 ? (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            {t('users.show.moveDialog.noResults')}
                          </div>
                        ) : (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            {t('users.show.moveDialog.typeToSearch')}
                          </div>
                        )}
                      </div>

                      {/* Warning note */}
                      {selectedOrg && (
                        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                          {t('users.show.moveDialog.roleResetNote')}
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => handleDialogOpenChange(false)}
                      >
                        {t('users.show.moveDialog.cancel')}
                      </Button>
                      <Button
                        onClick={handleMoveToOrganization}
                        disabled={!selectedOrg || isMoving}
                      >
                        {isMoving ? (
                          <>
                            <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                            {t('users.show.moveDialog.moving')}
                          </>
                        ) : (
                          t('users.show.moveDialog.confirm')
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                {!isDeactivated && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        disabled={isDeactivating}
                      >
                        <UserXIcon className="mr-2 h-4 w-4" />
                        {isDeactivating ? t('users.show.actions.deactivating') : t('users.show.actions.deactivate')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader className="items-center">
                        <div className="bg-amber-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                          <UserXIcon className="text-amber-600 size-6" />
                        </div>
                        <AlertDialogTitle>{t('users.show.deactivateDialog.title', { firstName: user.first_name, lastName: user.last_name })}</AlertDialogTitle>
                        <AlertDialogDescription className="text-center">
                          {t('users.show.deactivateDialog.description')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('users.show.deactivateDialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={handleDeactivate}
                          disabled={isDeactivating}
                        >
                          {isDeactivating ? t('users.show.deactivateDialog.confirming') : t('users.show.deactivateDialog.confirm')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {isDeactivated && (
                  <Badge variant="secondary" className="w-full justify-center py-2 bg-amber-100 text-amber-800">
                    {t('users.show.badges.deactivated')}
                  </Badge>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('users.show.statistics.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('users.show.statistics.accountAge')}</p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">
                    {t('users.show.statistics.days', { count: Math.floor((Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24)) })}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
